import React from 'react';
import { getTransactionList_Admin, approveTransaction_Admin, rejectTransaction_Admin, getAdminTransationStatistics } from "@/api/Admin/transaction";
import { TransactionAdminAPI, TransactionAdminStatsAPI } from "@/api/Admin/type";
import { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { Button, Modal, message, Input, Pagination, Spin } from "antd";
import { Helmet } from "react-helmet-async";
import {
    Search,
    Filter,
    Download,
    RefreshCw,
    Eye,
    Check,
    X
} from 'lucide-react';

const { TextArea } = Input;

// --- Types ---
type TransactionStatus = 'pending' | 'success' | 'cancelled';


// --- Helper Components ---

const StatusBadge = ({ status }: { status: string }) => {
    const statusLower = status?.toLowerCase() || '';
    const stylesMap: Record<string, string> = {
        pending: 'bg-yellow-100 text-yellow-800',
        succeed: 'bg-emerald-100 text-emerald-800',
        completed: 'bg-emerald-100 text-emerald-800',
        cancelled: 'bg-red-100 text-red-800',
        failed: 'bg-red-100 text-red-800',
        rejected: 'bg-red-100 text-red-800',
    };

    const labelsMap: Record<string, string> = {
        pending: 'Chờ duyệt',
        succeed: 'Thành công',
        completed: 'Thành công',
        cancelled: 'Đã hủy',
        failed: 'Thất bại',
        rejected: 'Từ chối',
    };

    return (
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${stylesMap[statusLower] || 'bg-gray-100 text-gray-800'}`}>
            {labelsMap[statusLower] || status}
        </span>
    );
};

const formatCurrency = (amount: any, currency: string = 'VND') => {
    const val = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency }).format(val || 0).replace('₫', currency);
};

// --- Main Component ---

export default function TransactionDashboard() {
    const [TransactionData, setTransactionData] = useState<TransactionAdminAPI | null>(null);
    const [statsData, setStatsData] = useState<{ label: string, value: string, change: string }[] | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<any | null>(null);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    const [actionModal, setActionModal] = useState<{ open: boolean, type: 'approve' | 'reject' | null, id: number | null }>({ open: false, type: null, id: null });
    const [actionNote, setActionNote] = useState('');
    const [receivedAmount, setReceivedAmount] = useState<number | undefined>(undefined);
    const [refundAmount, setRefundAmount] = useState<number | undefined>(undefined);

    const [loading, setLoading] = useState(false);

    const fetchData = (page: number = 1, pageSize: number = 10) => {
        setLoading(true);
        getTransactionList_Admin(page, pageSize).then(data => {
            setTransactionData(data);
            setTotalItems(data?.data?.total || 0);
            setPageSize(data?.data?.per_page || 10);
        }).catch(error => {
            console.error('Error fetching transaction data:', error);
            message.error('Failed to load transactions');
        }).finally(() => {
            setLoading(false);
        });
    };

        

    useEffect(() => {
        fetchData(page, pageSize);
    }, [page, pageSize]);

    useEffect(() => {
        getAdminTransationStatistics().then((stats: TransactionAdminStatsAPI) => {
            const statsArray = [
                { label: 'Tổng giao dịch', value: stats.data?.total_transactions.value.toLocaleString('vi-VN') || '0', change: `${stats.data?.total_transactions.percent || 0}%` },
                { label: 'Giao dịch thành công', value: stats.data?.success_transactions.value.toLocaleString('vi-VN') || '0', change: `${stats.data?.success_transactions.percent || 0}%` },
                { label: 'Giao dịch thất bại', value: stats.data?.pending_transactions.value.toLocaleString('vi-VN') || '0', change: `${stats.data?.pending_transactions.percent || 0}%` },
                { label: 'Doanh thu', value: formatCurrency(stats.data?.revenue.value || 0), change: `${stats.data?.revenue.percent || 0}%` },
            ];
            setStatsData(statsArray);
        }).catch(error => {
            console.error('Error fetching transaction statistics:', error);
            message.error('Failed to load transaction statistics');
        });
    }, []);

    const handleOpenActionModal = (type: 'approve' | 'reject', id: number) => {
        setActionModal({ open: true, type, id });
        setActionNote('');
        setReceivedAmount(undefined);
        setRefundAmount(undefined);
    };

    const handleConfirmAction = async () => {
        if (!actionModal.id || !actionModal.type) return;

        try {
            if (actionModal.type === 'approve') {
                await approveTransaction_Admin(actionModal.id, actionNote, receivedAmount, refundAmount);
            } else {
                await rejectTransaction_Admin(actionModal.id, actionNote, refundAmount);
            }
            message.success(`Transaction ${actionModal.type}d successfully`);
            fetchData(page, pageSize);
            handleCloseActionModal();
        } catch (error) {
            message.error(`Failed to ${actionModal.type} transaction`);
        }
    };

    const handleCloseActionModal = () => {
        setActionModal({ open: false, type: null, id: null });
        setActionNote('');
    };

    const handleViewDetail = (transaction: any) => {
        setSelectedTransaction(transaction);
        setIsModalOpen(true);
    };

    const getStatusClass = (status: string) => {
        switch (status.toLowerCase()) {
            case 'completed':
            case 'success':
                return styles.success;
            case 'pending':
                return styles.pending;
            case 'failed':
            case 'cancelled':
                return styles.failed;
            default:
                return '';
        }
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen font-sans">

            {/* 1. Header Title Section (Mới) */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Quản lý Giao dịch</h1>
                <p className="text-gray-500 mt-1">Theo dõi và quản lý tất cả các giao dịch của hệ thống</p>
            </div>

            {/* 2. Stats Cards Section (Mới) */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {statsData && statsData.map((stat, index) => (
                    <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between h-32">
                        <div className="flex justify-between items-start">
                            <span className="text-sm font-medium text-gray-500">{stat.label}</span>
                            <span className="text-emerald-600 text-xs font-bold bg-emerald-50 px-2 py-0.5 rounded-full">
                                {stat.change}
                            </span>
                        </div>
                        <div className="text-3xl font-bold text-gray-900 mt-2">
                            {stat.value}
                        </div>
                    </div>
                ))}
            </div>

            {/* 3. Main Table Container (Phần cũ) */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">

                {/* Filter Toolbar */}
                <div className="p-5 space-y-4">
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                            placeholder="Tìm kiếm theo tên, email, mã giao dịch..."
                        />
                    </div>

                    <div className="flex items-center space-x-2">
                        <div className="relative inline-block text-left">
                            <button className="inline-flex justify-between items-center w-48 px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                                <span className="flex items-center">
                                    <Filter className="h-4 w-4 mr-2 text-gray-500" />
                                    Tất cả trạng thái
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-col space-y-3 pt-2">
                        <button className="flex items-center justify-center px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm shadow-sm w-full sm:w-auto">
                            <Download className="h-4 w-4 mr-2" />
                            Xuất Excel
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto relative min-h-[200px]">
                    {loading && (
                        <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
                            <Spin size="large" />
                        </div>
                    )}
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {['ID', 'NGƯỜI DÙNG', 'LOẠI GIAO DỊCH', 'TIỀN THỰC NHẬN', 'PHƯƠNG THỨC', 'TRẠNG THÁI', 'MÃ GD', 'THỜI GIAN', 'THAO TÁC'].map((header) => (
                                    <th
                                        key={header}
                                        scope="col"
                                        className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap"
                                    >
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {!loading && (TransactionData?.data?.data || []).length === 0 && (
                                <tr>
                                    <td colSpan={10} className="px-6 py-10 text-center text-gray-500">
                                        Không có dữ liệu giao dịch
                                    </td>
                                </tr>
                            )}
                            {(TransactionData?.data?.data || []).map((tx) => (
                                <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{tx.id}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-bold text-gray-900">{tx.user_info.fullname}</div>
                                        <div className="text-sm text-gray-500">{tx.user_info.email}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-bold text-blue-600">{tx.transaction_type || 'N/A'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className={`text-sm font-bold text-emerald-600`}>
                                            {formatCurrency(tx.amount || 0, tx.currency)}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">Bank Transfer</div>
                                        {/* <div className="text-sm text-gray-500">{tx.currency}</div> */}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <StatusBadge status={tx.status} />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-medium">
                                        {tx.payment_code}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{new Date(tx.created_at).toLocaleTimeString()}</div>
                                        <div className="text-sm text-gray-500">{new Date(tx.created_at).toLocaleDateString()}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex items-center space-x-3">
                                            <button
                                                onClick={() => handleViewDetail(tx)}
                                                className="text-blue-500 hover:text-blue-700 bg-blue-50 p-1.5 rounded-md transition-colors">
                                                <Eye className="h-4 w-4" />
                                            </button>
                                            {tx.status === 'pending' && (
                                                <>
                                                    <button
                                                        onClick={() => handleOpenActionModal('approve', tx.id)}
                                                        className="text-emerald-500 hover:text-emerald-700 bg-emerald-50 p-1.5 rounded-md transition-colors">
                                                        <Check className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleOpenActionModal('reject', tx.id)}
                                                        className="text-red-500 hover:text-red-700 bg-red-50 p-1.5 rounded-md transition-colors">
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200 bg-gray-50 rounded-b-xl">
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                            <p className="text-sm text-gray-700">
                                Hiển thị <span className="font-medium">{totalItems > 0 ? (page - 1) * pageSize + 1 : 0}</span> đến <span className="font-medium">{Math.min(page * pageSize, totalItems)}</span> trong <span className="font-medium">{totalItems}</span> kết quả
                            </p>
                        </div>
                        <Pagination
                            current={page}
                            pageSize={pageSize}
                            total={totalItems}
                            onChange={(p, ps) => {
                                setPage(p);
                                setPageSize(ps);
                            }}
                            showSizeChanger
                            onShowSizeChange={(p, ps) => {
                                setPage(1);
                                setPageSize(ps);
                            }}
                            size="small"
                        />
                    </div>
                </div>
            </div>
            <Modal
                title="Transaction Details"
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={[
                    <Button key="close" onClick={() => setIsModalOpen(false)}>
                        Close
                    </Button>
                ]}
            >
                {selectedTransaction && (
                    <div className={styles.modalContent}>
                        <p><strong>ID:</strong> {selectedTransaction.id}</p>
                        <p><strong>Package Name:</strong> {selectedTransaction.user_package?.package_name_snapshot || 'N/A'}</p>
                        <p><strong>Transaction Code:</strong> {selectedTransaction.payment_code}</p>
                        <p><strong>User:</strong> {selectedTransaction.user_info.fullname}</p>
                        <p><strong>Email:</strong> {selectedTransaction.user_info.email}</p>
                        <p><strong>Amount:</strong> {Number(selectedTransaction.amount).toLocaleString('vi-VN')} {selectedTransaction.currency}</p>
                        <p><strong>Status:</strong> {selectedTransaction.status}</p>
                        <p><strong>Created At:</strong> {new Date(selectedTransaction.created_at).toLocaleString()}</p>
                        <div style={{ margin: '10px 0' }}></div>
                        <div><strong>Contract Info:</strong></div>
                        {selectedTransaction.user_package?.contract_info ? (
                            <>
                                <p className="!m-0"><strong>Full Name:</strong> {selectedTransaction.user_package.contract_info.full_name}</p>
                                <p className="!m-0"><strong>ID Number:</strong> {selectedTransaction.user_package.contract_info.id_number}</p>
                            </>
                        ) : (
                            <p>No contract info available.</p>
                        )}
                        <div style={{ margin: '10px 0' }}></div>
                        <div><strong>Files:</strong></div>
                        <a href={selectedTransaction.user_package?.contract_file || '#'} target="_blank" rel="noopener noreferrer">View Contract File</a>
                        <br />
                        <div style={{ margin: '10px 0' }}></div>
                        <div><strong>Transaction Image:</strong></div>
                        <img src={selectedTransaction.transaction_image_url} alt="Transaction" />
                        <a href={selectedTransaction.transaction_image_url} target="_blank" rel="noopener noreferrer">View Full Image</a>
                        {/* Add more fields here if available in your API response */}
                    </div>
                )}
            </Modal>

            <Modal
                title={`${actionModal.type === 'approve' ? 'Approve' : 'Reject'} Transaction`}
                open={actionModal.open}
                onCancel={handleCloseActionModal}
                onOk={handleConfirmAction}
                okText="Confirm"
                cancelText="Cancel"
                okButtonProps={{ danger: actionModal.type === 'reject' }}
            >
                <div style={{ padding: '20px 0' }}>
                    <p style={{ marginBottom: '8px' }}>
                        Are you sure you want to {actionModal.type} this transaction?
                    </p>
                    <label style={{ marginBottom: '4px', display: 'block' }}>Received Amount (if approving):</label>
                    <Input
                        style={{ marginBottom: '12px' }}
                        value={receivedAmount !== undefined ? receivedAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ''}
                        onChange={(e) => {
                            const value = e.target.value.replace(/,/g, "");
                            if (/^\d*$/.test(value)) {
                                setReceivedAmount(value ? Number(value) : undefined);
                            }
                        }}
                        placeholder="Received Amount (only for approval)"
                    />
                    <label style={{ marginBottom: '4px', display: 'block' }}>Refund Amount:</label>
                    <Input
                        style={{ marginBottom: '12px' }}
                        placeholder="Refund Amount"
                        value={refundAmount !== undefined ? refundAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : ''}
                        onChange={(e) => {
                            const value = e.target.value.replace(/,/g, "");
                            if (/^\d*$/.test(value)) {
                                setRefundAmount(value ? Number(value) : undefined);
                            }
                        }}
                        type="number"
                    />
                    <div style={{ marginBottom: '8px' }}>Note:</div>
                    <TextArea
                        rows={4}
                        value={actionNote}
                        onChange={(e) => setActionNote(e.target.value)}
                        placeholder="Enter a note (optional)"
                    />
                </div>
            </Modal>
        </div>
    );
}